import React from "react";
import { usd, usdSigned, DIVISIONS, StaticNote } from "./ui";
import "./Dashboard.css";

// Per-client change between the previous and current upload, grouped by division,
// with a "change since last upload" total. Matches the legacy app's Deltas page.
export default function Deltas({ data }) {
  const { totals, context, monthLabel } = data;
  const d = data.deltas;

  const est = context.prevMonthEstimated;
  const prevVal = context.prevMonthFinal != null ? (est ? "~" : "") + usd(context.prevMonthFinal) : "—";
  const prevLabel = `Prior month ${est ? "(est.)" : "final"} (${context.prevMonth})`;
  const prevSub = est ? "est. until P&L finalized" : "closed month";

  if (!d || !d.hasPrevious) {
    return (
      <div className="dash">
        <section className="kpis kpis-3">
          <div className="kpi">
            <div className="kpi-label">{prevLabel}</div>
            <div className="kpi-value">{prevVal}</div>
            {est && <div className="kpi-sub">{prevSub}</div>}
          </div>
          <div className="kpi kpi-primary">
            <div className="kpi-label">{monthLabel} forecast</div>
            <div className="kpi-value">{usd(totals.completedOngoing)}</div>
            <div className="kpi-sub">completed / ongoing only</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Change since last upload</div>
            <div className="kpi-value">—</div>
          </div>
        </section>
        <StaticNote>{d?.message || "Upload a second CSV to see per-client changes since the previous upload."}</StaticNote>
      </div>
    );
  }

  const rows = d.clients;
  const byDiv = DIVISIONS.map((div) => ({ div, rows: rows.filter((r) => r.division === div) }))
    .filter((g) => g.rows.length);
  const other = rows.filter((r) => !DIVISIONS.includes(r.division));
  if (other.length) byDiv.push({ div: "Other", rows: other });

  return (
    <div className="dash">
      <section className="kpis kpis-3">
        <div className="kpi">
          <div className="kpi-label">{prevLabel}</div>
          <div className="kpi-value">{prevVal}</div>
          <div className="kpi-sub">{prevSub}</div>
        </div>
        <div className="kpi kpi-primary">
          <div className="kpi-label">{monthLabel} forecast</div>
          <div className="kpi-value">{usd(d.currTotal)}</div>
          <div className="kpi-sub">completed / ongoing only</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Change since {d.prevReportDate}</div>
          <div className={"kpi-value " + (d.changeSinceLast >= 0 ? "pos-text" : "neg-text")}>{usdSigned(d.changeSinceLast)}</div>
          <div className="kpi-sub">vs last upload</div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>Revenue Changes by Client</h2>
          <span>{d.prevReportDate} → {d.currReportDate}</span>
        </div>
        <table className="grid">
          <thead>
            <tr>
              <th>Client</th><th>Division</th>
              <th>{d.prevReportDate}</th><th>{d.currReportDate}</th>
              <th>Change</th><th>% Change</th>
            </tr>
          </thead>
          <tbody>
            {byDiv.map((g) => (
              <React.Fragment key={g.div}>
                <tr className="group-row"><td colSpan={6}><span className={"pill pill-" + g.div.toLowerCase()}>{g.div}</span></td></tr>
                {g.rows.map((r) => {
                  const cls = r.change > 0 ? "pos-text" : r.change < 0 ? "neg-text" : "muted";
                  return (
                    <tr key={r.name} className={r.change > 0 ? "row-pos" : r.change < 0 ? "row-neg" : ""}>
                      <td className="rowhead">{r.name}</td>
                      <td><span className={"pill pill-" + r.division.toLowerCase()}>{r.division}</span></td>
                      <td>{usd(r.prev)}</td>
                      <td>{usd(r.curr)}</td>
                      <td className={cls}>{r.change === 0 ? "—" : usdSigned(r.change)}</td>
                      <td className={cls}>{r.isNew ? "new" : r.dropped ? "dropped" : r.pct == null ? "—" : (r.pct >= 0 ? "+" : "") + r.pct + "%"}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
